import { useRef, useMemo } from "react";
import { Palette, Image as ImageIcon, Upload, X, Loader2, LayoutList, LayoutGrid, Type, Smartphone, Sparkles, Star, MousePointerClick, Wand2, MessageSquare, PaintBucket } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useConfig } from "./ConfiguracoesLayout";
import ConfigSection, { inputClass } from "./ConfigSection";

const FONT_OPTIONS = [
  { value: "Inter", label: "Inter", style: "'Inter', sans-serif" },
  { value: "Poppins", label: "Poppins", style: "'Poppins', sans-serif" },
  { value: "Roboto", label: "Roboto", style: "'Roboto', sans-serif" },
  { value: "Montserrat", label: "Montserrat", style: "'Montserrat', sans-serif" },
  { value: "Playfair Display", label: "Playfair Display", style: "'Playfair Display', serif" },
  { value: "Lato", label: "Lato", style: "'Lato', sans-serif" },
  { value: "Nunito", label: "Nunito", style: "'Nunito', sans-serif" },
  { value: "Space Grotesk", label: "Space Grotesk", style: "'Space Grotesk', sans-serif" },
];

const LAYOUT_OPTIONS = [
  { value: "list", label: "Lista", icon: LayoutList, desc: "Items em linhas com imagem lateral" },
  { value: "grid", label: "Grid", icon: LayoutGrid, desc: "Cards em grade com imagem em cima" },
];

const CARD_STYLES = [
  { value: "rounded", label: "Arredondado", preview: "rounded-2xl" },
  { value: "sharp", label: "Reto", preview: "rounded-none" },
  { value: "pill", label: "Pílula", preview: "rounded-3xl" },
];

const HEADER_STYLES = [
  { value: "classic", label: "Clássico", desc: "Capa + card com logo e info" },
  { value: "compact", label: "Compacto", desc: "Sem capa, logo ao lado do nome" },
  { value: "hero", label: "Hero", desc: "Capa grande, logo centralizado" },
];

const BUTTON_STYLES = [
  { value: "filled", label: "Preenchido", desc: "Fundo colorido" },
  { value: "outline", label: "Contorno", desc: "Borda colorida" },
  { value: "rounded", label: "Arredondado", desc: "Pílula arredondada" },
];

const Aparencia = () => {
  const { form, update, uploadImage, uploadingLogo, uploadingCover, logoInputRef, coverInputRef } = useConfig();

  const accentColor = form.menu_accent_color || form.primary_color || "#f97316";
  const categoryColor = form.menu_category_color || accentColor;

  // Load Google Fonts dynamically
  const fontLink = useMemo(() => {
    const font = form.menu_font?.replace(/ /g, "+") || "Inter";
    return `https://fonts.googleapis.com/css2?family=${font}:wght@400;500;600;700&display=swap`;
  }, [form.menu_font]);

  return (
    <div className="space-y-6">
      <link rel="stylesheet" href={fontLink} />

      <ConfigSection title="Aparência do Menu Digital" icon={Palette}>
        {/* Colors */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
            <Palette className="h-3.5 w-3.5 text-primary" /> Cores
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <ColorPicker label="Cor principal" value={form.primary_color} onChange={(v) => update("primary_color", v)} />
            <ColorPicker label="Cor de destaque" value={form.menu_accent_color || form.primary_color} onChange={(v) => update("menu_accent_color", v)} />
            <ColorPicker label="Fundo" value={form.menu_bg_color} onChange={(v) => update("menu_bg_color", v)} />
            <ColorPicker label="Texto" value={form.menu_text_color} onChange={(v) => update("menu_text_color", v)} />
            <ColorPicker label="Categorias" value={form.menu_category_color || accentColor} onChange={(v) => update("menu_category_color", v)} />
          </div>
          <button
            onClick={() => {
              update("primary_color", "#f97316");
              update("menu_accent_color", "");
              update("menu_bg_color", "#ffffff");
              update("menu_text_color", "#222222");
              update("menu_category_color", "");
            }}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors underline"
          >
            Restaurar cores padrão
          </button>
        </div>

        {/* Font */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
            <Type className="h-3.5 w-3.5 text-primary" /> Fonte
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {FONT_OPTIONS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => update("menu_font", f.value)}
                className={`text-left px-3 py-2.5 rounded-lg border transition-colors ${
                  form.menu_font === f.value
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-card text-muted-foreground hover:border-primary/50"
                }`}
                style={{ fontFamily: f.style }}
              >
                <span className="text-sm font-semibold">{f.label}</span>
                <p className="text-[11px] mt-0.5 opacity-70" style={{ fontFamily: f.style }}>
                  Cardápio digital
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Layout */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
            <LayoutList className="h-3.5 w-3.5 text-primary" /> Layout do Cardápio
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {LAYOUT_OPTIONS.map((l) => (
              <button
                key={l.value}
                type="button"
                onClick={() => update("menu_layout", l.value)}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-colors ${
                  form.menu_layout === l.value
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-card text-muted-foreground hover:border-primary/50"
                }`}
              >
                <l.icon className="h-6 w-6" />
                <span className="text-xs font-semibold">{l.label}</span>
                <span className="text-[10px] opacity-70">{l.desc}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Card Style */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-foreground">Estilo dos Cards</h3>
          <div className="flex gap-2">
            {CARD_STYLES.map((s) => (
              <button
                key={s.value}
                type="button"
                onClick={() => update("menu_card_style", s.value)}
                className={`flex-1 flex flex-col items-center gap-2 p-3 border transition-colors ${s.preview} ${
                  form.menu_card_style === s.value
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-card text-muted-foreground hover:border-primary/50"
                }`}
              >
                <div className={`w-full h-6 bg-muted ${s.preview}`} />
                <span className="text-[11px] font-semibold">{s.label}</span>
              </button>
            ))}
          </div>
        </div>
      </ConfigSection>

      {/* Header Style */}
      <ConfigSection title="Estilo do Cabeçalho" icon={ImageIcon}>
        <div className="grid grid-cols-3 gap-2">
          {HEADER_STYLES.map((h) => (
            <button
              key={h.value}
              type="button"
              onClick={() => update("menu_header_style", h.value)}
              className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition-colors text-center ${
                (form.menu_header_style || "classic") === h.value
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border bg-card text-muted-foreground hover:border-primary/50"
              }`}
            >
              <div className="w-full h-10 rounded-lg overflow-hidden relative" style={{ backgroundColor: `${accentColor}20` }}>
                {h.value === "classic" && (
                  <>
                    <div className="w-full h-5" style={{ backgroundColor: `${accentColor}40` }} />
                    <div className="absolute bottom-0 left-2 w-4 h-4 rounded-sm border" style={{ backgroundColor: accentColor, borderColor: '#fff' }} />
                  </>
                )}
                {h.value === "compact" && (
                  <div className="flex items-center gap-1.5 p-1.5">
                    <div className="w-5 h-5 rounded-sm" style={{ backgroundColor: accentColor }} />
                    <div className="flex-1 space-y-0.5">
                      <div className="h-1.5 w-10 rounded-full" style={{ backgroundColor: `${accentColor}60` }} />
                      <div className="h-1 w-6 rounded-full" style={{ backgroundColor: `${accentColor}30` }} />
                    </div>
                  </div>
                )}
                {h.value === "hero" && (
                  <>
                    <div className="w-full h-full" style={{ backgroundColor: `${accentColor}40` }} />
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <div className="w-4 h-4 rounded-sm border" style={{ backgroundColor: accentColor, borderColor: '#fff' }} />
                    </div>
                  </>
                )}
              </div>
              <span className="text-[11px] font-semibold">{h.label}</span>
              <span className="text-[9px] opacity-70 leading-tight">{h.desc}</span>
            </button>
          ))}
        </div>
      </ConfigSection>

      {/* Button Style */}
      <ConfigSection title="Estilo dos Botões" icon={MousePointerClick}>
        <div className="grid grid-cols-3 gap-2">
          {BUTTON_STYLES.map((b) => (
            <button
              key={b.value}
              type="button"
              onClick={() => update("menu_button_style", b.value)}
              className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition-colors ${
                (form.menu_button_style || "filled") === b.value
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border bg-card text-muted-foreground hover:border-primary/50"
              }`}
            >
              <div className="flex items-center justify-center h-8 w-full">
                {b.value === "filled" && (
                  <div className="px-3 py-1.5 text-[10px] font-bold text-white rounded-lg" style={{ backgroundColor: accentColor }}>
                    Adicionar
                  </div>
                )}
                {b.value === "outline" && (
                  <div className="px-3 py-1.5 text-[10px] font-bold rounded-lg border-2" style={{ borderColor: accentColor, color: accentColor }}>
                    Adicionar
                  </div>
                )}
                {b.value === "rounded" && (
                  <div className="px-3 py-1.5 text-[10px] font-bold text-white rounded-full" style={{ backgroundColor: accentColor }}>
                    Adicionar
                  </div>
                )}
              </div>
              <span className="text-[11px] font-semibold">{b.label}</span>
            </button>
          ))}
        </div>
      </ConfigSection>

      {/* Welcome Message */}
      <ConfigSection title="Mensagem de Boas-vindas" icon={MessageSquare}>
        <div className="space-y-2">
          <Label>Banner no topo do cardápio</Label>
          <Textarea
            value={form.menu_welcome_message || ""}
            onChange={(e) => update("menu_welcome_message", e.target.value)}
            placeholder="Ex: 🎉 Hoje com 10% OFF em tudo! ou Bem-vindo ao nosso cardápio!"
            className={inputClass}
            rows={2}
          />
          <p className="text-[11px] text-muted-foreground">Deixe em branco para não exibir nenhum banner.</p>
        </div>
      </ConfigSection>

      {/* Animations Toggle */}
      <ConfigSection title="Animações" icon={Wand2}>
        <div className="flex items-center justify-between">
          <div>
            <Label>Animações suaves no cardápio</Label>
            <p className="text-xs text-muted-foreground mt-0.5">Efeitos de fade-in nos produtos e transições suaves</p>
          </div>
          <Switch checked={form.menu_animations_enabled !== false} onCheckedChange={(v) => update("menu_animations_enabled", v)} />
        </div>
      </ConfigSection>

      {/* Logo & Cover */}
      <ConfigSection title="Logo e Capa" icon={ImageIcon}>
        {/* Logo Upload */}
        <div className="space-y-2">
          <Label className="flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" /> Logo do Restaurante</Label>
          <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadImage(f, "logo"); e.target.value = ""; }} />
          <div className="flex items-start gap-4">
            {form.logo_url ? (
              <div className="relative w-20 h-20 rounded-xl border border-border overflow-hidden shrink-0 bg-secondary">
                <img src={form.logo_url} alt="Logo" className="w-full h-full object-cover" />
                <button onClick={() => update("logo_url", "")} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center"><X className="h-3 w-3" /></button>
              </div>
            ) : (
              <div className="w-20 h-20 rounded-xl border-2 border-dashed border-border flex items-center justify-center bg-secondary/50 shrink-0"><ImageIcon className="h-6 w-6 text-muted-foreground" /></div>
            )}
            <div className="flex-1 space-y-2">
              <Button type="button" variant="outline" size="sm" disabled={uploadingLogo} onClick={() => logoInputRef.current?.click()} className="gap-1.5">
                {uploadingLogo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                {uploadingLogo ? "Enviando..." : "Enviar logo"}
              </Button>
              <p className="text-[11px] text-muted-foreground">Recomendado: 512x512px, PNG ou JPG</p>
            </div>
          </div>
        </div>

        {/* Cover Upload */}
        <div className="space-y-2">
          <Label className="flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" /> Imagem de Capa</Label>
          <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadImage(f, "cover"); e.target.value = ""; }} />
          {form.cover_url ? (
            <div className="relative w-full h-36 rounded-xl border border-border overflow-hidden bg-secondary">
              <img src={form.cover_url} alt="Capa" className="w-full h-full object-cover" />
              <button onClick={() => update("cover_url", "")} className="absolute top-2 right-2 w-6 h-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center"><X className="h-3.5 w-3.5" /></button>
            </div>
          ) : (
            <button type="button" onClick={() => coverInputRef.current?.click()} disabled={uploadingCover} className="w-full h-36 rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center bg-secondary/50 hover:bg-secondary transition-colors gap-2">
              {uploadingCover ? <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /> : (<><Upload className="h-6 w-6 text-muted-foreground" /><span className="text-xs text-muted-foreground">Clique para enviar a imagem de capa</span><span className="text-[10px] text-muted-foreground">Recomendado: 1200x400px</span></>)}
            </button>
          )}
          {form.cover_url && (
            <Button type="button" variant="outline" size="sm" disabled={uploadingCover} onClick={() => coverInputRef.current?.click()} className="gap-1.5">
              {uploadingCover ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Trocar capa
            </Button>
          )}
        </div>
      </ConfigSection>

      {/* Live Preview */}
      <ConfigSection title="Preview ao Vivo" icon={Smartphone}>
        <div className="flex justify-center">
          <div
            className="w-[320px] border-[6px] border-foreground/20 rounded-[2rem] overflow-hidden shadow-xl"
            style={{ backgroundColor: form.menu_bg_color || "#ffffff", fontFamily: `'${form.menu_font || "Inter"}', sans-serif` }}
          >
            {/* Mini header based on style */}
            {(form.menu_header_style || "classic") === "compact" ? (
              <div className="px-4 py-3 flex items-center gap-3">
                {form.logo_url ? (
                  <img src={form.logo_url} alt="" className="w-9 h-9 rounded-lg object-cover border" style={{ borderColor: `${form.menu_text_color}15` }} />
                ) : (
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: accentColor }}>
                    {form.name?.charAt(0) || "R"}
                  </div>
                )}
                <div>
                  <p className="font-bold text-sm" style={{ color: form.menu_text_color }}>{form.name || "Nome do Restaurante"}</p>
                  <p className="text-[9px] opacity-60" style={{ color: form.menu_text_color }}>
                    {form.description || "Descrição"}
                  </p>
                </div>
              </div>
            ) : (form.menu_header_style || "classic") === "hero" ? (
              <div className="h-32 relative" style={{ backgroundColor: accentColor }}>
                {form.cover_url && <img src={form.cover_url} alt="" className="w-full h-full object-cover" />}
                <div className="absolute inset-0 bg-black/30 flex flex-col items-center justify-center text-center px-4">
                  {form.logo_url ? (
                    <img src={form.logo_url} alt="" className="w-14 h-14 rounded-2xl object-cover border-2 border-white shadow-lg mb-1.5" />
                  ) : (
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-lg mb-1.5" style={{ backgroundColor: accentColor }}>
                      {form.name?.charAt(0) || "R"}
                    </div>
                  )}
                  <p className="font-bold text-sm text-white drop-shadow">{form.name || "Nome do Restaurante"}</p>
                  <p className="text-[9px] text-white/80">{form.description || "Descrição"}</p>
                </div>
              </div>
            ) : (
              <>
                <div className="h-24 relative" style={{ backgroundColor: accentColor }}>
                  {form.cover_url && <img src={form.cover_url} alt="" className="w-full h-full object-cover" />}
                  <div className="absolute -bottom-5 left-4">
                    {form.logo_url ? (
                      <img src={form.logo_url} alt="" className="w-11 h-11 rounded-xl object-cover border-2 border-white shadow-md" />
                    ) : (
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-md" style={{ backgroundColor: accentColor }}>
                        {form.name?.charAt(0) || "R"}
                      </div>
                    )}
                  </div>
                </div>
                <div className="px-4 pt-7 pb-3">
                  <p className="font-bold text-sm" style={{ color: form.menu_text_color }}>{form.name || "Nome do Restaurante"}</p>
                  <p className="text-[10px] mt-0.5 opacity-60" style={{ color: form.menu_text_color }}>
                    {form.description || "Descrição do restaurante"}
                  </p>
                </div>
              </>
            )}

            {/* Welcome message preview */}
            {form.menu_welcome_message && (
              <div className="mx-3 mb-2 px-3 py-2 rounded-xl text-[10px] font-medium text-center" style={{ backgroundColor: `${accentColor}15`, color: accentColor }}>
                {form.menu_welcome_message}
              </div>
            )}

            {/* Fake categories with category color */}
            <div className="px-4 pb-2 flex gap-1.5">
              <div className="px-3 py-1 text-[10px] font-semibold text-white" style={{ backgroundColor: categoryColor, borderRadius: form.menu_card_style === "sharp" ? "0" : form.menu_card_style === "pill" ? "999px" : "8px" }}>
                Destaques
              </div>
              <div className="px-3 py-1 text-[10px] font-medium opacity-50" style={{ color: form.menu_text_color, backgroundColor: `${form.menu_text_color}10`, borderRadius: form.menu_card_style === "sharp" ? "0" : form.menu_card_style === "pill" ? "999px" : "8px" }}>
                Pratos
              </div>
              <div className="px-3 py-1 text-[10px] font-medium opacity-50" style={{ color: form.menu_text_color, backgroundColor: `${form.menu_text_color}10`, borderRadius: form.menu_card_style === "sharp" ? "0" : form.menu_card_style === "pill" ? "999px" : "8px" }}>
                Bebidas
              </div>
            </div>

            {/* Fake products */}
            <div className="px-4 pb-4 space-y-2">
              {form.menu_layout === "list" ? (
                <>
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex gap-2 p-2 border" style={{ borderColor: `${form.menu_text_color}15`, borderRadius: form.menu_card_style === "sharp" ? "0" : form.menu_card_style === "pill" ? "20px" : "12px" }}>
                      <div className="flex-1">
                        <div className="h-2.5 w-20 rounded-sm opacity-70" style={{ backgroundColor: form.menu_text_color }} />
                        <div className="h-2 w-28 rounded-sm mt-1.5 opacity-30" style={{ backgroundColor: form.menu_text_color }} />
                        <div className="flex items-center gap-1.5 mt-2">
                          <div className="h-2.5 w-12 rounded-sm font-bold opacity-80" style={{ backgroundColor: accentColor }} />
                          {/* Preview button style */}
                          {(form.menu_button_style || "filled") === "outline" ? (
                            <div className="w-5 h-5 rounded-md border" style={{ borderColor: accentColor }} />
                          ) : (form.menu_button_style || "filled") === "rounded" ? (
                            <div className="w-5 h-5 rounded-full" style={{ backgroundColor: accentColor }} />
                          ) : (
                            <div className="w-5 h-5 rounded-md" style={{ backgroundColor: accentColor }} />
                          )}
                        </div>
                      </div>
                      <div className="w-14 h-14 shrink-0 bg-muted" style={{ borderRadius: form.menu_card_style === "sharp" ? "0" : form.menu_card_style === "pill" ? "14px" : "8px" }} />
                    </div>
                  ))}
                </>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="border overflow-hidden" style={{ borderColor: `${form.menu_text_color}15`, borderRadius: form.menu_card_style === "sharp" ? "0" : form.menu_card_style === "pill" ? "20px" : "12px" }}>
                      <div className="w-full h-16 bg-muted" />
                      <div className="p-2">
                        <div className="h-2.5 w-16 rounded-sm opacity-70" style={{ backgroundColor: form.menu_text_color }} />
                        <div className="flex items-center gap-1.5 mt-2">
                          <div className="h-2.5 w-10 rounded-sm opacity-80" style={{ backgroundColor: accentColor }} />
                          {(form.menu_button_style || "filled") === "outline" ? (
                            <div className="w-5 h-5 rounded-md border" style={{ borderColor: accentColor }} />
                          ) : (form.menu_button_style || "filled") === "rounded" ? (
                            <div className="w-5 h-5 rounded-full" style={{ backgroundColor: accentColor }} />
                          ) : (
                            <div className="w-5 h-5 rounded-md" style={{ backgroundColor: accentColor }} />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </ConfigSection>

      {/* Store Rating Display */}
      <ConfigSection title="Avaliação no Cardápio" icon={Star}>
        <div className="flex items-center justify-between">
          <div>
            <Label>Mostrar avaliação da loja no cardápio digital</Label>
            <p className="text-xs text-muted-foreground mt-0.5">Exibe a nota média e quantidade de avaliações no topo do cardápio</p>
          </div>
          <Switch checked={form.show_rating} onCheckedChange={(v) => update("show_rating", v)} />
        </div>
      </ConfigSection>

      {/* Smart Menu */}
      <ConfigSection title="Cardápio Inteligente" icon={Sparkles}>
        <div className="flex items-center justify-between">
          <div>
            <Label>Reordenação automática por horário</Label>
            <p className="text-xs text-muted-foreground mt-0.5">Reordena os produtos automaticamente com base na popularidade por faixa horária (manhã, almoço, tarde, noite)</p>
          </div>
          <Switch checked={form.smart_menu_enabled} onCheckedChange={(v) => update("smart_menu_enabled", v)} />
        </div>
      </ConfigSection>

      {/* AI Combo Agent */}
      <ConfigSection title="Agente de Combos IA" icon={Sparkles}>
        <div className="flex items-center justify-between">
          <div>
            <Label>Ativar sugestões de combos com IA</Label>
            <p className="text-xs text-muted-foreground mt-0.5">O assistente IA sugere combos automaticamente no cardápio digital</p>
          </div>
          <Switch checked={form.ai_combo_enabled} onCheckedChange={(v) => update("ai_combo_enabled", v)} />
        </div>
        {form.ai_combo_enabled && (
          <div className="space-y-2 pt-2">
            <Label>Desconto dos combos (%)</Label>
            <Input
              type="number"
              min="1"
              max="50"
              value={form.ai_combo_discount_percent}
              onChange={(e) => update("ai_combo_discount_percent", e.target.value)}
              className={inputClass}
              placeholder="10"
            />
            <p className="text-[11px] text-muted-foreground">Percentual de desconto aplicado nos combos sugeridos pela IA</p>
          </div>
        )}
      </ConfigSection>
    </div>
  );
};

const ColorPicker = ({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) => (
  <div className="space-y-1.5">
    <Label className="text-xs">{label}</Label>
    <div className="flex items-center gap-2">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-9 h-9 rounded-lg border border-border cursor-pointer shrink-0" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} className="h-9 bg-card border-border text-xs flex-1" />
    </div>
  </div>
);

export default Aparencia;
